import { useState, useMemo } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useUsage } from '@/lib/query/hooks/useUsage';
import { useCompanies, useCompanyAccounts } from '@/lib/query/hooks/useCompanies';
import { useMiseSellersCurrent } from '@/lib/query/hooks/useMiseSellers';
import { AppLayout } from '@/components/layout/AppLayout';
import {
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip as MuiTooltip,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

function formatDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function UsagePage() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  // デフォルト: 過去30日
  const defaultEnd = useMemo(() => formatDate(new Date()), []);
  const defaultStart = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return formatDate(d);
  }, []);

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');

  const { data: companies = [], isLoading: companiesLoading } = useCompanies();
  const {
    data: usage,
    isLoading: usageLoading,
    error: usageError,
  } = useUsage(startDate, endDate, selectedCompanyId || undefined);

  // 企業選択時: アカウント取得 → 店舗一覧取得 → seller_id → 店舗名マップ構築
  const { data: companyAccounts = [] } = useCompanyAccounts(selectedCompanyId);
  const firstAccountId = companyAccounts.length > 0 ? companyAccounts[0].id : '';
  const { data: sellers = [] } = useMiseSellersCurrent(firstAccountId);

  const sellerNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const seller of sellers) {
      map.set(seller.id, seller.name);
    }
    return map;
  }, [sellers]);

  /** seller_id を店舗名に変換。不明な場合は「店舗不明」 */
  const resolveSellerName = (sellerId: string): string => {
    if (!sellerId || sellerId === '-') return '店舗不明';
    return sellerNameMap.get(sellerId) ?? '店舗不明';
  };

  // 店舗別アクセス数: seller_id → 店舗名に変換
  const sellerAccessData = useMemo(() => {
    if (!usage?.seller_access) return [];
    return usage.seller_access.map((entry) => ({
      ...entry,
      seller_name: resolveSellerName(entry.seller_id),
    }));
  }, [usage?.seller_access, sellerNameMap]);

  // 店舗別エンドポイント利用数: seller_id → 店舗名に変換
  const sellerEndpointAccessData = useMemo(() => {
    if (!usage?.seller_endpoint_access) return [];
    return usage.seller_endpoint_access.map((entry) => ({
      ...entry,
      seller_name: resolveSellerName(entry.seller_id),
    }));
  }, [usage?.seller_endpoint_access, sellerNameMap]);

  // 店舗別時間帯アクセス: recharts用にピボット変換（時間帯 × 店舗名）
  const SELLER_COLORS = [
    '#1976d2',
    '#e91e63',
    '#ff9800',
    '#4caf50',
    '#9c27b0',
    '#00bcd4',
    '#ff5722',
    '#607d8b',
    '#795548',
    '#3f51b5',
  ];

  const sellerHourlyChartData = useMemo(() => {
    if (!usage?.seller_hourly_access || usage.seller_hourly_access.length === 0)
      return { data: [], sellerNames: [] as string[] };

    // ユニークな店舗名を収集
    const sellerIdSet = new Set<string>();
    for (const entry of usage.seller_hourly_access) {
      sellerIdSet.add(entry.seller_id);
    }
    const sellerNames = Array.from(sellerIdSet).map(resolveSellerName);
    const sellerIdToName = new Map<string, string>();
    for (const sid of sellerIdSet) {
      sellerIdToName.set(sid, resolveSellerName(sid));
    }

    // 時間帯でグループ化し、各店舗名の値をフラットに展開
    const hourMap = new Map<string, Record<string, number>>();
    for (const entry of usage.seller_hourly_access) {
      const name = sellerIdToName.get(entry.seller_id) ?? '店舗不明';
      const hourKey = entry.hour.split(' ').pop()?.slice(0, 5) ?? entry.hour;
      if (!hourMap.has(hourKey)) {
        hourMap.set(hourKey, { hour: 0 } as unknown as Record<string, number>);
      }
      const record = hourMap.get(hourKey)!;
      record['hour'] = 0; // placeholder, will use hourKey
      record[name] = (record[name] ?? 0) + entry.access_count;
    }

    const data = Array.from(hourMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([hourKey, record]) => ({
        hour: hourKey,
        ...record,
      }));

    return { data, sellerNames: Array.from(new Set(sellerNames)) };
  }, [usage?.seller_hourly_access, sellerNameMap]);

  if (authLoading) {
    return (
      <AppLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </AppLayout>
    );
  }

  if (!isAuthenticated) {
    return (
      <AppLayout>
        <Alert severity="warning">この機能を利用するにはログインが必要です。</Alert>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="mise利用状況">
      <Box sx={{ p: 3 }}>
        {/* 日付範囲セレクタ */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <Typography variant="subtitle1" fontWeight="bold">
              期間
            </Typography>
            <TextField
              type="date"
              size="small"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <Typography>〜</Typography>
            <TextField
              type="date"
              size="small"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              select
              size="small"
              label="企業"
              value={selectedCompanyId}
              onChange={(e) => setSelectedCompanyId(e.target.value)}
              disabled={companiesLoading}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">すべての企業</MenuItem>
              {companies.map((company) => (
                <MenuItem key={company.company_id} value={company.company_id}>
                  {company.company_id}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Paper>

        {usageLoading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {usageError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            利用状況の取得に失敗しました。
          </Alert>
        )}

        {usage && (
          <Grid container spacing={3}>
            {/* DAU推移 */}
            <Grid size={{ xs: 12, lg: 6 }}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Typography variant="h6">DAU推移（日次アクティブユーザー数）</Typography>
                  <MuiTooltip
                    title="APIアクセスログからユーザーIDのユニーク数（count_distinct）を日別に集計しています。"
                    arrow
                  >
                    <InfoOutlinedIcon fontSize="small" color="action" sx={{ cursor: 'pointer' }} />
                  </MuiTooltip>
                </Box>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={usage.dau}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12 }}
                      tickFormatter={(v) => v.slice(5)} // MM-DD
                    />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="dau"
                      stroke="#1976d2"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      name="DAU"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>

            {/* 時間帯別アクセス推移 */}
            <Grid size={{ xs: 12, lg: 6 }}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Typography variant="h6">時間帯別アクセス推移</Typography>
                  <MuiTooltip
                    title="APIアクセスログから1時間ごとのリクエスト数（count）を集計し、時系列で表示しています。"
                    arrow
                  >
                    <InfoOutlinedIcon fontSize="small" color="action" sx={{ cursor: 'pointer' }} />
                  </MuiTooltip>
                </Box>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={usage.hourly_access}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="hour" tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="access_count"
                      stroke="#66bb6a"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      name="アクセス数"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>

            {/* エンドポイント別アクセス数 */}
            <Grid size={{ xs: 12, lg: 6 }}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Typography variant="h6">エンドポイント別アクセス数</Typography>
                  <MuiTooltip
                    title="APIアクセスログからエンドポイントごとのリクエスト総数（count）を集計し、多い順に表示しています。"
                    arrow
                  >
                    <InfoOutlinedIcon fontSize="small" color="action" sx={{ cursor: 'pointer' }} />
                  </MuiTooltip>
                </Box>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={usage.endpoint_access} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis type="category" dataKey="endpoint" width={180} tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="access_count" fill="#42a5f5" name="アクセス数" />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>

            {/* 企業別アクティブユーザー */}
            <Grid size={{ xs: 12, lg: 6 }}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Typography variant="h6">企業別アクティブユーザー数</Typography>
                  <MuiTooltip
                    title="APIアクセスログから企業ごとのユニークユーザー数（count_distinct）を集計し、多い順に表示しています。"
                    arrow
                  >
                    <InfoOutlinedIcon fontSize="small" color="action" sx={{ cursor: 'pointer' }} />
                  </MuiTooltip>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>企業ID</TableCell>
                        <TableCell align="right">アクティブユーザー数</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {usage.company_active_users.map((row) => (
                        <TableRow key={row.company_id}>
                          <TableCell>{row.company_id}</TableCell>
                          <TableCell align="right">{row.active_users}</TableCell>
                        </TableRow>
                      ))}
                      {usage.company_active_users.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={2} align="center">
                            データがありません
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>

            {/* レスポンスタイム分析 */}
            <Grid size={12}>
              <Paper sx={{ p: 2 }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Typography variant="h6">レスポンスタイム分析</Typography>
                  <MuiTooltip
                    title="APIアクセスログからエンドポイントごとの応答時間を集計しています。平均（avg）・最大（max）・95パーセンタイル（p95）を表示しています。"
                    arrow
                  >
                    <InfoOutlinedIcon fontSize="small" color="action" sx={{ cursor: 'pointer' }} />
                  </MuiTooltip>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>エンドポイント</TableCell>
                        <TableCell align="right">平均 (ms)</TableCell>
                        <TableCell align="right">最大 (ms)</TableCell>
                        <TableCell align="right">P95 (ms)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {usage.response_times.map((row) => (
                        <TableRow key={row.endpoint}>
                          <TableCell>{row.endpoint}</TableCell>
                          <TableCell align="right">{row.avg_ms.toFixed(1)}</TableCell>
                          <TableCell align="right">{row.max_ms.toFixed(1)}</TableCell>
                          <TableCell align="right">{row.p95_ms.toFixed(1)}</TableCell>
                        </TableRow>
                      ))}
                      {usage.response_times.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            データがありません
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>

            {/* 店舗別セクション（企業選択時のみ表示） */}
            {selectedCompanyId && (
              <>
                {/* 店舗別アクセス数 */}
                <Grid size={{ xs: 12, lg: 6 }}>
                  <Paper sx={{ p: 2, height: '100%' }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Typography variant="h6">店舗別アクセス数</Typography>
                      <MuiTooltip
                        title="APIアクセスログからseller_idごとのリクエスト総数（count）を集計し、多い順に表示しています。seller_idが取得できないリクエストは「店舗不明」として集約しています。"
                        arrow
                      >
                        <InfoOutlinedIcon
                          fontSize="small"
                          color="action"
                          sx={{ cursor: 'pointer' }}
                        />
                      </MuiTooltip>
                    </Box>
                    {sellerAccessData.length > 0 ? (
                      <ResponsiveContainer
                        width="100%"
                        height={Math.max(300, sellerAccessData.length * 40)}
                      >
                        <BarChart data={sellerAccessData} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" />
                          <YAxis
                            type="category"
                            dataKey="seller_name"
                            width={200}
                            tick={{ fontSize: 12 }}
                          />
                          <Tooltip />
                          <Bar dataKey="access_count" fill="#42a5f5" name="アクセス数" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <Alert severity="info">店舗別アクセスデータがありません</Alert>
                    )}
                  </Paper>
                </Grid>

                {/* 店舗別API利用数 */}
                <Grid size={{ xs: 12, lg: 6 }}>
                  <Paper sx={{ p: 2, height: '100%' }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Typography variant="h6">店舗別API利用数</Typography>
                      <MuiTooltip
                        title="APIアクセスログからseller_id・エンドポイントごとのリクエスト数を集計し、多い順に表示しています。"
                        arrow
                      >
                        <InfoOutlinedIcon
                          fontSize="small"
                          color="action"
                          sx={{ cursor: 'pointer' }}
                        />
                      </MuiTooltip>
                    </Box>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>店舗名</TableCell>
                            <TableCell>エンドポイント</TableCell>
                            <TableCell align="right">アクセス数</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sellerEndpointAccessData.map((row, idx) => (
                            <TableRow key={`${row.seller_id}-${row.endpoint}-${idx}`}>
                              <TableCell>{row.seller_name}</TableCell>
                              <TableCell>{row.endpoint}</TableCell>
                              <TableCell align="right">{row.access_count}</TableCell>
                            </TableRow>
                          ))}
                          {sellerEndpointAccessData.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={3} align="center">
                                データがありません
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Grid>

                {/* 店舗別時間帯アクセス推移 */}
                <Grid size={12}>
                  <Paper sx={{ p: 2 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Typography variant="h6">店舗別時間帯アクセス推移</Typography>
                      <MuiTooltip
                        title="APIアクセスログからseller_id・1時間ごとのリクエスト数を集計し、店舗別に色分けして時系列で表示しています。"
                        arrow
                      >
                        <InfoOutlinedIcon
                          fontSize="small"
                          color="action"
                          sx={{ cursor: 'pointer' }}
                        />
                      </MuiTooltip>
                    </Box>
                    {sellerHourlyChartData.data.length > 0 ? (
                      <ResponsiveContainer width="100%" height={350}>
                        <LineChart data={sellerHourlyChartData.data}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="hour" tick={{ fontSize: 12 }} />
                          <YAxis allowDecimals={false} />
                          <Tooltip />
                          <Legend />
                          {sellerHourlyChartData.sellerNames.map((name, idx) => (
                            <Line
                              key={name}
                              type="monotone"
                              dataKey={name}
                              stroke={SELLER_COLORS[idx % SELLER_COLORS.length]}
                              strokeWidth={2}
                              dot={{ r: 3 }}
                              name={name}
                            />
                          ))}
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <Alert severity="info">店舗別時間帯アクセスデータがありません</Alert>
                    )}
                  </Paper>
                </Grid>
              </>
            )}
          </Grid>
        )}
      </Box>
    </AppLayout>
  );
}
